import React, { useMemo } from "react";
import SupportTicketSelect from "./SupportTicketSelect";
import "../styles/pagination.css";

interface PaginationProps {
    currentPage: number;      // Zero-based (0 = first page)
    totalPages: number;
    totalElements?: number;
    onPageChange: (page: number) => void;
    pageSize?: number;
    onPageSizeChange?: (size: number) => void;
    /** Options for the page-size selector. Defaults to 10 / 25 / 50 / 100. */
    pageSizeOptions?: number[];
}

const DEFAULT_PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

export default function Pagination({
    currentPage,
    totalPages,
    totalElements,
    onPageChange,
    pageSize = 25,
    onPageSizeChange,
    pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
}: PaginationProps) {
    const pages = Array.from({ length: Math.max(totalPages, 0) }, (_, i) => i);

    // Show max 5 page buttons centered on current page
    const visiblePages = pages.slice(
        Math.max(0, currentPage - 2),
        Math.min(totalPages, currentPage + 3)
    );

    const sizeOptions = useMemo(
        () =>
            pageSizeOptions.map((size) => ({
                value: String(size),
                label: `${size} per page`,
            })),
        [pageSizeOptions],
    );

    return (
        <div className="pagination" data-testid="pagination">
            {/* Summary */}
            {totalElements !== undefined && (
                <div className="pagination-info">
                    <span>
                        Showing{" "}
                        <strong>
                            {totalElements === 0
                                ? "0-0"
                                : `${currentPage * pageSize + 1}-${Math.min((currentPage + 1) * pageSize, totalElements)}`}
                        </strong>{" "}
                        of <strong>{totalElements}</strong>
                    </span>
                </div>
            )}

            {/* Page Controls */}
            <div className="pagination-controls">
                <div className="page-nav">
                    <button
                        className="page-btn"
                        onClick={() => onPageChange(currentPage - 1)}
                        disabled={currentPage === 0 || totalPages <= 1}
                        data-testid="pagination-prev"
                    >
                        Previous
                    </button>

                    {visiblePages.map(page => (
                        <button
                            key={page}
                            className={`page-number ${page === currentPage ? 'page-number-active' : ''}`}
                            onClick={() => onPageChange(page)}
                            data-testid={`pagination-page-${page}`}
                        >
                            {page + 1}
                        </button>
                    ))}

                    <button
                        className="page-btn"
                        onClick={() => onPageChange(currentPage + 1)}
                        disabled={currentPage >= totalPages - 1 || totalPages <= 1}
                        data-testid="pagination-next"
                    >
                        Next
                    </button>
                </div>
            </div>

            {/* Page Size Selector — same custom light dropdown as table filters */}
            {onPageSizeChange && (
                <div className="pagination-size-select">
                    <SupportTicketSelect
                        value={String(pageSize)}
                        options={sizeOptions}
                        size="compact"
                        data-testid="pagination-size-select"
                        onChange={(next) => onPageSizeChange(Number(next))}
                    />
                </div>
            )}
        </div>
    );
}
